import { useState } from "react";
import { Plus, Trash2, IndianRupee, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  INDUSTRIES, COMPANY_STATUSES, COMPANY_STATUS_STYLES_LIGHT,
  DEAL_STAGES, DEAL_STAGE_STYLES_LIGHT, INPUT_LIGHT, SELECT_CONTENT_LIGHT,
  formatINR, fmtDate, avatarTone, initialsOf,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CompanyBadge = ({ status }) => (
  <Badge variant="outline" className={`${COMPANY_STATUS_STYLES_LIGHT[status] || "border-slate-200 bg-slate-100 text-slate-500"} uppercase tracking-[0.15em] text-[10px]`} data-testid={`company-status-${status}`}>
    {status}
  </Badge>
);

export const CrmPanel = ({ companies, deals, onRefresh }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [notesDraft, setNotesDraft] = useState("");
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: "", industry: INDUSTRIES[0], website: "", status: "lead" });
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", designation: "" });
  const [dealForm, setDealForm] = useState({ title: "", value: "", stage: "new", expected_close: "" });

  const selected = companies.find((c) => c.id === selectedId) || null;
  const companyDeals = selected ? deals.filter((d) => d.company_id === selected.id) : [];
  const openValue = deals.filter((d) => ["new", "negotiation"].includes(d.stage)).reduce((s, d) => s + (Number(d.value) || 0), 0);
  const wonValue = deals.filter((d) => d.stage === "won").reduce((s, d) => s + (Number(d.value) || 0), 0);

  const selectCompany = async (c) => {
    setSelectedId(c.id);
    setNotesDraft(c.notes || "");
    try {
      const { data } = await api.get(`/crm/companies/${c.id}/contacts`);
      setContacts(data);
    } catch {
      setContacts([]);
    }
  };

  const handle = (fn, successMsg) => async (...args) => {
    try {
      await fn(...args);
      if (successMsg) toast.success(successMsg);
      onRefresh();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    }
  };

  const addCompany = handle(async (e) => {
    e.preventDefault();
    await api.post("/crm/companies", { ...companyForm, website: companyForm.website || null });
    setShowCompanyForm(false);
    setCompanyForm({ name: "", industry: INDUSTRIES[0], website: "", status: "lead" });
  }, "Company added to CRM.");

  const updateCompany = handle(async (fields) => {
    await api.put(`/crm/companies/${selected.id}`, { ...selected, ...fields });
  });

  const deleteCompany = handle(async () => {
    if (!window.confirm(`Delete ${selected.name} and its contacts + deals?`)) return;
    await api.delete(`/crm/companies/${selected.id}`);
    setSelectedId(null);
  }, "Company deleted.");

  const addContact = handle(async (e) => {
    e.preventDefault();
    const { data } = await api.post(`/crm/companies/${selected.id}/contacts`, {
      name: contactForm.name,
      email: contactForm.email || null,
      phone: contactForm.phone || null,
      designation: contactForm.designation || null,
    });
    setContacts((cs) => [data, ...cs]);
    setContactForm({ name: "", email: "", phone: "", designation: "" });
  }, "Contact added.");

  const deleteContact = handle(async (contactId) => {
    await api.delete(`/crm/contacts/${contactId}`);
    setContacts((cs) => cs.filter((c) => c.id !== contactId));
  }, "Contact removed.");

  const addDeal = handle(async (e) => {
    e.preventDefault();
    await api.post("/crm/deals", {
      company_id: selected.id,
      title: dealForm.title,
      value: Number(dealForm.value) || 0,
      stage: dealForm.stage,
      expected_close: dealForm.expected_close || null,
    });
    setDealForm({ title: "", value: "", stage: "new", expected_close: "" });
  }, "Deal created.");

  const updateDealStage = handle(async (deal, stage) => {
    await api.put(`/crm/deals/${deal.id}`, { ...deal, stage });
  }, "Deal updated.");

  const deleteDeal = handle(async (dealId) => {
    await api.delete(`/crm/deals/${dealId}`);
  }, "Deal deleted.");

  return (
    <div data-testid="crm-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          ["Companies", companies.length],
          ["Active deals", deals.filter((d) => ["new", "negotiation"].includes(d.stage)).length],
          ["Open pipeline", formatINR(openValue)],
          ["Won business", formatINR(wonValue)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`crm-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="font-display text-2xl font-semibold text-slate-900">{value}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Companies ({companies.length})</p>
            <button
              onClick={() => setShowCompanyForm(!showCompanyForm)}
              data-testid="crm-add-company-btn"
              className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-medium text-white transition-shadow duration-300 hover:shadow-glow"
            >
              <Plus size={13} /> Add company
            </button>
          </div>

          {showCompanyForm && (
            <form onSubmit={addCompany} className="mb-5 rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="crm-company-form">
              <div className="space-y-2">
                <Label className="text-slate-500">Company name</Label>
                <Input required value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  data-testid="crm-company-name-input" className={INPUT_LIGHT} placeholder="Acme Corp" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-500">Industry</Label>
                  <Select value={companyForm.industry} onValueChange={(v) => setCompanyForm({ ...companyForm, industry: v })}>
                    <SelectTrigger data-testid="crm-company-industry-select" className={INPUT_LIGHT}><SelectValue /></SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_LIGHT}>
                      {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Status</Label>
                  <Select value={companyForm.status} onValueChange={(v) => setCompanyForm({ ...companyForm, status: v })}>
                    <SelectTrigger data-testid="crm-company-status-select" className={INPUT_LIGHT}><SelectValue /></SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_LIGHT}>
                      {COMPANY_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`crm-company-status-${s}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-500">Website</Label>
                <Input value={companyForm.website} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                  data-testid="crm-company-website-input" className={INPUT_LIGHT} placeholder="https://" />
              </div>
              <button type="submit" data-testid="crm-company-save-btn" className="rounded-full bg-brand px-6 py-2.5 text-xs font-medium text-white transition-shadow duration-300 hover:shadow-glow">
                Save company
              </button>
            </form>
          )}

          <div className="space-y-3">
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => selectCompany(c)}
                data-testid={`crm-company-${c.id}`}
                className={`w-full text-left rounded-2xl border p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${
                  selectedId === c.id ? "border-brand/60 bg-orange-50/60" : "border-slate-200 bg-white hover:border-brand/40"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="flex items-center gap-3 font-display font-semibold text-slate-900">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(c.name)}`}>
                      {initialsOf(c.name)}
                    </span>
                    {c.name}
                  </p>
                  <CompanyBadge status={c.status} />
                </div>
                <p className="mt-1.5 pl-12 text-xs text-slate-400">{c.industry || "Industry not set"} · added {fmtDate(c.created_at)}</p>
              </button>
            ))}
            {companies.length === 0 && (
              <p className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400" data-testid="crm-no-companies">
                No companies yet — add one, or convert an enquiry into a lead.
              </p>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          {!selected ? (
            <div className="flex h-full min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-slate-300" data-testid="crm-no-selection">
              <p className="font-editorial italic text-2xl text-slate-400">Select a company to manage it.</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="crm-company-detail">
              <div className="rounded-2xl border border-slate-200 bg-white p-7">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="flex items-center gap-3 font-display text-2xl font-semibold text-slate-900" data-testid="crm-detail-name">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold ${avatarTone(selected.name)}`}>
                        {initialsOf(selected.name)}
                      </span>
                      {selected.name}
                    </h3>
                    <p className="mt-1 pl-14 text-xs text-slate-400">{selected.industry || "—"} {selected.owner ? `· owner: ${selected.owner}` : ""}</p>
                    {selected.website && (
                      <a href={selected.website} target="_blank" rel="noopener noreferrer" data-testid="crm-detail-website"
                        className="mt-1.5 flex items-center gap-1.5 pl-14 text-xs text-brand hover:underline">
                        <Globe size={12} /> {selected.website}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={selected.status} onValueChange={(v) => updateCompany({ status: v })}>
                      <SelectTrigger data-testid="crm-detail-status-select" className={`h-9 w-32 text-xs ${INPUT_LIGHT}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_LIGHT}>
                        {COMPANY_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`crm-detail-status-${s}`}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={deleteCompany} data-testid="crm-delete-company-btn"
                      className="flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-xs text-rose-500 transition-colors duration-200 hover:border-rose-400">
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
                <div className="mt-5 space-y-2">
                  <Label className="text-slate-500">Account notes</Label>
                  <Textarea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                    data-testid="crm-notes-input" className={INPUT_LIGHT} placeholder="Relationship context, hiring patterns, key preferences..." />
                  <button onClick={() => updateCompany({ notes: notesDraft })} data-testid="crm-notes-save-btn"
                    className="rounded-full border border-slate-200 px-5 py-2 text-xs text-slate-600 transition-colors duration-200 hover:border-brand/60 hover:text-brand">
                    Save notes
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="crm-contacts-section">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Contacts ({contacts.length})</p>
                <div className="mt-4 space-y-3">
                  {contacts.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/70 p-4" data-testid={`crm-contact-${c.id}`}>
                      <div className="flex items-center gap-3">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarTone(c.name)}`}>
                          {initialsOf(c.name)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{c.name} {c.designation ? <span className="font-normal text-slate-400">· {c.designation}</span> : null}</p>
                          <p className="text-xs text-slate-400">{c.email || ""} {c.phone ? `· ${c.phone}` : ""}</p>
                        </div>
                      </div>
                      <button onClick={() => deleteContact(c.id)} data-testid={`crm-contact-delete-${c.id}`} className="text-slate-300 hover:text-rose-500 transition-colors duration-200">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <form onSubmit={addContact} className="mt-4 grid grid-cols-2 gap-3" data-testid="crm-contact-form">
                  <Input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    data-testid="crm-contact-name-input" className={INPUT_LIGHT} placeholder="Contact name" />
                  <Input value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })}
                    data-testid="crm-contact-designation-input" className={INPUT_LIGHT} placeholder="Designation" />
                  <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    data-testid="crm-contact-email-input" className={INPUT_LIGHT} placeholder="Email" />
                  <div className="flex gap-3">
                    <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      data-testid="crm-contact-phone-input" className={INPUT_LIGHT} placeholder="Phone" />
                    <button type="submit" data-testid="crm-contact-add-btn" className="shrink-0 rounded-full bg-brand px-4 text-white transition-shadow duration-300 hover:shadow-glow">
                      <Plus size={15} />
                    </button>
                  </div>
                </form>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="crm-deals-section">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Deals ({companyDeals.length})</p>
                <div className="mt-4 space-y-3">
                  {companyDeals.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/70 p-4" data-testid={`crm-deal-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <IndianRupee size={15} className="text-brand" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{d.title}</p>
                          <p className="text-xs text-slate-400">{formatINR(d.value)} {d.expected_close ? `· closes ${fmtDate(d.expected_close)}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`${DEAL_STAGE_STYLES_LIGHT[d.stage]} uppercase tracking-[0.15em] text-[10px]`}>{d.stage}</Badge>
                        <Select value={d.stage} onValueChange={(v) => updateDealStage(d, v)}>
                          <SelectTrigger data-testid={`crm-deal-stage-${d.id}`} className={`h-8 w-32 text-xs ${INPUT_LIGHT}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={SELECT_CONTENT_LIGHT}>
                            {DEAL_STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`crm-deal-stage-option-${d.id}-${s}`}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button onClick={() => deleteDeal(d.id)} data-testid={`crm-deal-delete-${d.id}`} className="text-slate-300 hover:text-rose-500 transition-colors duration-200">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {companyDeals.length === 0 && <p className="text-xs text-slate-400">No deals yet for this company.</p>}
                </div>
                <form onSubmit={addDeal} className="mt-4 grid grid-cols-2 gap-3" data-testid="crm-deal-form">
                  <Input required value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })}
                    data-testid="crm-deal-title-input" className={INPUT_LIGHT} placeholder="Deal title — e.g. 10 React contractors" />
                  <Input type="number" min="0" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })}
                    data-testid="crm-deal-value-input" className={INPUT_LIGHT} placeholder="Value in ₹ (e.g. 1500000)" />
                  <Select value={dealForm.stage} onValueChange={(v) => setDealForm({ ...dealForm, stage: v })}>
                    <SelectTrigger data-testid="crm-deal-stage-select" className={INPUT_LIGHT}><SelectValue /></SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_LIGHT}>
                      {DEAL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-3">
                    <Input type="date" value={dealForm.expected_close} onChange={(e) => setDealForm({ ...dealForm, expected_close: e.target.value })}
                      data-testid="crm-deal-close-input" className={INPUT_LIGHT} />
                    <button type="submit" data-testid="crm-deal-add-btn" className="shrink-0 rounded-full bg-brand px-5 text-xs font-medium text-white transition-shadow duration-300 hover:shadow-glow">
                      Add deal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
